/**
 * BulkWinderAIEnhance — the winder binding of the shared bulk AI enhancement
 * dialog.
 *
 * The flow, the grid and every word of the copy are unchanged: this file used
 * to hold its own 860-line copy of {@link BulkEntityAIEnhance}, which was in
 * turn a copy of BulkCalibreAIEnhance. What is left is the part that was ever
 * actually about winders — which callable to run, which registry to read, and
 * the nouns.
 *
 * ⚠️ `formatWinderValue` here, NOT `formatSuggestion` from bulkEnrichFields.
 *
 * The shared grid used the generic formatter once, and the nested
 * `turnsPerDayRange` rendered as a literal "[object Object]". Display was the
 * visible half. The load-bearing half is that every proposal is string-compared
 * against `getWinderExisting` — which formats ranges as "1-1999" — to decide
 * `cellAgreement`. Two formatters that disagree make a range that AGREES with
 * the document read as `differs`: the cell coloured amber, and the apply pass
 * stopped skipping it, rewriting an identical range and re-triggering the
 * Algolia indexer for every winder the model merely confirmed. One formatter on
 * both sides is what makes that comparison mean anything, which is why the
 * descriptor carries it rather than the dialog assuming one.
 */
import { useMemo } from 'react';
import { enrichWinderWithAI, type WinderEnrichResult } from '../../services/firebase';
import type { WinderEntry } from '../../types';
import {
  WINDER_ALWAYS_APPLY_KEYS,
  WINDER_ENRICH_FIELD_ORDER,
  buildWinderPatch,
  coerceWinderValue,
  formatWinderValue,
  getWinderConfidence,
  getWinderExisting,
  getWinderFieldLabel,
  getWinderSuggestion,
  suggestedWinderKeys,
} from '../../services/winderEnrichFields';
import {
  BulkEntityAIEnhance,
  type BulkEnhanceDescriptor,
} from '../common/BulkEntityAIEnhance';

const WINDERS_COLLECTION = 'winders';

export const WINDER_BULK_ENHANCE: BulkEnhanceDescriptor<WinderEntry, WinderEnrichResult> = {
  collection: WINDERS_COLLECTION,
  formName: 'winderEnrich',
  // Rows are enriched from their stored `make` + `model`, so no per-row
  // Firestore read is needed: the selection the list already holds is enough.
  enrich: (entry) =>
    enrichWinderWithAI(entry.make, entry.model, { winderId: entry.id }),

  fieldOrder: WINDER_ENRICH_FIELD_ORDER,
  suggestedKeys: (result) => suggestedWinderKeys(result),
  fieldLabel: getWinderFieldLabel,
  getExisting: getWinderExisting,
  getSuggestion: getWinderSuggestion,
  getConfidence: getWinderConfidence,
  coerceValue: coerceWinderValue,
  formatValue: formatWinderValue,
  buildPatch: buildWinderPatch,
  alwaysApplyKeys: WINDER_ALWAYS_APPLY_KEYS,

  rowLabel: (entry) => entry.model,
  rowSubLabel: (entry) => entry.make,
  copy: {
    title: (count) => `AI Appliance Enhancement — ${count} selected`,
    rowHeader: 'Winder',
    runButton: (count) => `Run AI on ${count} winder${count === 1 ? '' : 's'}`,
    runComplete: (ok) => `Enhanced ${ok} appliance${ok === 1 ? '' : 's'} — review below`,
    applied: (fields, rows) =>
      `Applied ${fields} field${fields === 1 ? '' : 's'} across ${rows} appliance${rows === 1 ? '' : 's'}`,
    appliedWithFailures: (rows, failed) =>
      `Applied ${rows} appliance${rows === 1 ? '' : 's'}, ${failed} failed`,
    failures: (count) => `${count} appliance(s) failed to enhance`,
    configNote: (
      <>
        The threshold + mode decide which cells start <em>checked</em> in the review
        grid. Every proposed value is shown regardless — you can always check a cell
        manually. In “fill empty” mode, fields that already have a value are
        highlighted and start unchecked; a stored <em>No</em> counts as a value, so an
        explicit “not lockable” is never overwritten by a fill run.{' '}
        <strong>
          Description starts checked whenever it meets the confidence threshold
        </strong>{' '}
        (independent of the fill/overwrite mode). Make and model are never written by
        this tool — they feed the document id.
      </>
    ),
    legendNote: (
      <>
        Confidence is <em>per field</em> for winders — the dot beside each value is
        what the threshold reads; the dot beside the winder name is the best
        confidence in that row. A cell that agrees with what is already stored writes
        nothing and is skipped on apply.
      </>
    ),
  },
};

interface BulkWinderAIEnhanceProps {
  isOpen: boolean;
  onClose: () => void;
  /** The currently-selected winders to enhance. */
  winders: WinderEntry[];
  /** Called after a write-back commits, so the host can refresh + clear selection. */
  onApplied: () => void;
}

export function BulkWinderAIEnhance({
  isOpen,
  onClose,
  winders,
  onApplied,
}: BulkWinderAIEnhanceProps) {
  // Stable identity so the dialog's memoised column/default work isn't
  // invalidated on every render of the listing above it.
  const descriptor = useMemo(() => WINDER_BULK_ENHANCE, []);
  return (
    <BulkEntityAIEnhance
      isOpen={isOpen}
      onClose={onClose}
      entries={winders}
      onApplied={onApplied}
      descriptor={descriptor}
    />
  );
}
